import React, { useState, useRef } from 'react';
import styles from './InteractiveTileGrid.module.scss';

export interface TilePair {
  isVisible?: boolean;
  id: string;
  kpiTile: React.ReactNode;
  kpiDetailTile: React.ReactNode;
}

interface InteractiveTileGridProps {
  tiles: TilePair[];
  className?: string;
  onTileSelect?: (selectedIds: string[]) => void;
  maxDetailTiles?: number;
  gridColumns?: number; // number of columns when in grid layout (no tiles selected)
}

const InteractiveTileGrid: React.FC<InteractiveTileGridProps> = ({
  tiles,
  className = '',
  onTileSelect,
  gridColumns,
}) => {
  const [selectedTileIds, setSelectedTileIds] = useState<string[]>([]);
  const visibleTiles = tiles.filter((tile) => tile.isVisible !== false);
  const tilesContainerRef = useRef<HTMLDivElement>(null);
  const dummyScrollRef = useRef<HTMLDivElement>(null);
  const scrollingSource = useRef<'dummy' | 'tiles' | null>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const hasSelection = selectedTileIds.length > 0;

  const getTileWidth = () => {
    if (tilesContainerRef.current) {
      const styleWidth = tilesContainerRef.current.style.getPropertyValue('--tile-width');
      if (styleWidth) return parseInt(styleWidth, 10);
    }
    return 390; // Default fallback
  };

  const scrollToTile = (tileId: string) => {
    const container = tilesContainerRef.current;
    if (!container) return;

    const tileIndex = visibleTiles.findIndex((t) => t.id === tileId);
    if (tileIndex === -1) return;

    const tileWidth = getTileWidth();
    const gap = 16;
    const tilePosition = tileIndex * (tileWidth + gap);

    // Check if tile is fully visible
    const { scrollLeft, clientWidth } = container;
    const tileEnd = tilePosition + tileWidth;

    if (tilePosition < scrollLeft || tileEnd > scrollLeft + clientWidth) {
      container.scrollTo({
        left: tilePosition,
        behavior: 'smooth',
      });
    }
  };

  const handleDummyScroll = () => {
    if (scrollingSource.current === 'tiles') return;

    const dummy = dummyScrollRef.current;
    const tiles = tilesContainerRef.current;
    if (!dummy || !tiles) return;

    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollingSource.current = 'dummy';

    const dummyMaxScroll = dummy.scrollWidth - dummy.clientWidth;
    const tilesMaxScroll = tiles.scrollWidth - tiles.clientWidth;

    if (dummyMaxScroll > 0) {
      const ratio = dummy.scrollLeft / dummyMaxScroll;
      tiles.scrollLeft = ratio * tilesMaxScroll;
    }

    scrollTimeout.current = setTimeout(() => {
      scrollingSource.current = null;
    }, 50);
  };

  const handleTilesScroll = () => {
    if (scrollingSource.current === 'dummy') return;

    const dummy = dummyScrollRef.current;
    const tiles = tilesContainerRef.current;
    if (!dummy || !tiles) return;

    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollingSource.current = 'tiles';

    const dummyMaxScroll = dummy.scrollWidth - dummy.clientWidth;
    const tilesMaxScroll = tiles.scrollWidth - tiles.clientWidth;

    if (tilesMaxScroll > 0) {
      const ratio = tiles.scrollLeft / tilesMaxScroll;
      dummy.scrollLeft = ratio * dummyMaxScroll;
    }

    scrollTimeout.current = setTimeout(() => {
      scrollingSource.current = null;
    }, 100);
  };

  const handleTileClick = (tileId: string) => {
    setSelectedTileIds((prev) => {
      let newSelection: string[];

      if (prev.includes(tileId)) {
        newSelection = prev.filter((id) => id !== tileId);
      } else {
        // Capture the current tile width before switching to single-row layout
        const container = tilesContainerRef.current;
        if (container && prev.length === 0) {
          const firstTile = container.querySelector(`.${styles.tile_wrapper}`);
          if (firstTile) {
            const tileWidth = (firstTile as HTMLElement).offsetWidth;
            container.style.setProperty('--tile-width', `${tileWidth}px`);
          }
        }

        newSelection = [...prev, tileId];
        // Scroll to the newly selected tile after state update
        setTimeout(() => scrollToTile(tileId), 0);
      }

      onTileSelect?.(newSelection);
      return newSelection;
    });
  };

  // Calculate total content width for dummy scroll
  const getDummyContentWidth = () => {
    const tileWidth = getTileWidth();
    const gap = 16;
    // Total width of all tiles + gaps
    return visibleTiles.length * tileWidth + (visibleTiles.length - 1) * gap;
  };

  const handleCloseDetail = (tileId: string) => {
    setSelectedTileIds((prev) => {
      const newSelection = prev.filter((id) => id !== tileId);

      // Clear the tile width CSS variable when returning to grid layout
      if (newSelection.length === 0) {
        const container = tilesContainerRef.current;
        if (container) {
          container.style.removeProperty('--tile-width');
        }
      }

      onTileSelect?.(newSelection);
      return newSelection;
    });
  };

  let styleOverride = {};
  if (gridColumns) {
    styleOverride = { '--grid-columns': gridColumns };
  }

  return (
    <div className={`${styles.interactive_tile_grid_wrapper} ${className}`}>
      <div className={styles.tiles_row_wrapper}>
        <div
          ref={tilesContainerRef}
          className={`${styles.tiles_container} ${
            hasSelection ? styles.single_row : styles.grid_layout
          }`}
          style={styleOverride}
          onScroll={hasSelection ? handleTilesScroll : undefined}
        >
          {visibleTiles.map((tile) => {
            const isSelected = selectedTileIds.includes(tile.id);

            return (
              <div
                key={tile.id}
                className={`${styles.tile_wrapper} ${
                  isSelected ? styles.selected : ''
                } ${hasSelection ? styles.compact : ''}`}
                onClick={() => handleTileClick(tile.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTileClick(tile.id);
                  }
                }}
                data-testid={`tile-wrapper-${tile.id}`}
              >
                {tile.kpiTile}
              </div>
            );
          })}
        </div>

        {hasSelection && (
          <div
            ref={dummyScrollRef}
            className={styles.dummy_scroll_container}
            onScroll={handleDummyScroll}
          >
            <div style={{ width: `${getDummyContentWidth()}px` }} />
          </div>
        )}
      </div>

      {hasSelection && (
        <div className={styles.detail_tiles_container}>
          {selectedTileIds.map((tileId) => {
            const tile = visibleTiles.find((t) => t.id === tileId);
            if (!tile) return null;

            return (
              <div key={tileId} className={styles.detail_tile_wrapper}>
                {React.cloneElement(tile.kpiDetailTile as React.ReactElement, {
                  onClose: () => handleCloseDetail(tileId),
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InteractiveTileGrid;
